import React, { useState, useEffect, useCallback } from 'react';
import { Upload, FileText, Download, Trash2, File, Image, FileSpreadsheet, FileArchive, X, Send, Lock, AlertTriangle, CheckCircle, Calendar } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'Ko', 'Mo', 'Go'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileIcon = (filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return <Image size={24} className="text-purple-500" />;
  if (['xls', 'xlsx', 'csv'].includes(ext)) return <FileSpreadsheet size={24} className="text-green-600" />;
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return <FileArchive size={24} className="text-yellow-600" />;
  if (['pdf'].includes(ext)) return <FileText size={24} className="text-red-500" />;
  if (['doc', 'docx'].includes(ext)) return <FileText size={24} className="text-blue-600" />;
  return <File size={24} className="text-gray-500" />;
};

const AGDocumentsTab = ({ assemblee, userRole, token, onDocumentsChange }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);
  const [convocationStatus, setConvocationStatus] = useState(null);
  const [sendingConvocation, setSendingConvocation] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const fetchDocuments = useCallback(async () => {
    if (!assemblee?.id || !token) return;
    try {
      const response = await axios.get(`${API}/assemblees/${assemblee.id}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (err) {
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  }, [assemblee?.id, token]);

  const fetchConvocationStatus = useCallback(async () => {
    if (!assemblee?.id || !token) return;
    try {
      const response = await axios.get(`${API}/assemblees/${assemblee.id}/convocation-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConvocationStatus(response.data);
    } catch (err) {
      console.error('Error fetching convocation status:', err);
    }
  }, [assemblee?.id, token]);

  useEffect(() => {
    fetchDocuments();
    fetchConvocationStatus();
  }, [fetchDocuments, fetchConvocationStatus]);

  const handleSendConvocation = async () => {
    if (!assemblee?.id || !token) return;
    
    setSendingConvocation(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${API}/assemblees/${assemblee.id}/send-convocation`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh status
      await fetchConvocationStatus();
      setShowConfirmModal(false);
      
      // Show success message
      alert(`✅ Convocation envoyée avec succès !\n\n` +
            `📧 ${response.data.emails_sent} email(s) envoyé(s)\n` +
            `📎 ${response.data.documents_sent} document(s) joint(s)\n` +
            `📅 ${response.data.days_before_ag} jours avant l'AG\n\n` +
            `⚠️ Les documents sont maintenant verrouillés.`);
      
      if (onDocumentsChange) onDocumentsChange();
    } catch (err) {
      console.error('Error sending convocation:', err);
      setError(err.response?.data?.detail || 'Erreur lors de l\'envoi de la convocation');
    } finally {
      setSendingConvocation(false);
    }
  };

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    setError(null);
    setUploadProgress(0);
    
    const uploadFile = async (file) => {
      // Check file size (100 MB max)
      if (file.size > 100 * 1024 * 1024) {
        throw new Error(`Le fichier "${file.name}" dépasse 100 Mo`);
      }
      
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(
        `${API}/assemblees/${assemblee.id}/documents`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      );
    };
    
    try {
      for (const file of files) {
        await uploadFile(file);
      }
      await fetchDocuments();
      if (onDocumentsChange) onDocumentsChange();
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err.response?.data?.detail || err.message || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    
    try {
      await axios.delete(`${API}/assemblees/${assemblee.id}/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchDocuments();
      if (onDocumentsChange) onDocumentsChange();
    } catch (err) {
      console.error('Error deleting document:', err);
      setError(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleDownload = async (doc) => {
    try {
      setError(null);
      const response = await axios.get(
        `${API}/assemblees/${assemblee.id}/documents/${doc.id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading document:', err);
      if (err.response?.status === 404) {
        setError('Document introuvable sur le serveur');
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError('Erreur lors du téléchargement. Veuillez réessayer.');
      }
    }
  };

  // Download all documents at once
  const handleDownloadAll = async () => {
    if (documents.length === 0) return;
    
    setError(null);
    for (const doc of documents) {
      try {
        await handleDownload(doc);
        // Small delay between downloads to prevent browser blocking
        await new Promise(resolve => setTimeout(resolve, 300));
      } catch (err) {
        console.error(`Error downloading ${doc.original_name}:`, err);
      }
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleUpload(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleUpload(Array.from(e.target.files));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00D3A8]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between">
          <span className="text-red-700 text-sm">{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Convocation Status Banner for Gestionnaire/Directeur */}
      {(userRole === 'gestionnaire' || userRole === 'directeur_cabinet') && convocationStatus && (
        <div className={`rounded-xl p-4 border ${
          convocationStatus.convocation_sent 
            ? 'bg-green-50 border-green-200' 
            : convocationStatus.can_send_now 
              ? 'bg-blue-50 border-blue-200'
              : 'bg-amber-50 border-amber-200'
        }`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start space-x-3">
              {convocationStatus.convocation_sent ? (
                <>
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="text-green-600" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-green-800">Convocation envoyée</h3>
                    <p className="text-sm text-green-700">
                      Envoyée le {new Date(convocationStatus.convocation_sent_at).toLocaleDateString('fr-FR', { 
                        day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                      })}
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      {convocationStatus.emails_sent} coproprietaire(s) notifié(s) • Documents verrouillés
                    </p>
                  </div>
                </>
              ) : convocationStatus.can_send_now ? (
                <>
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Send className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-blue-800">Prêt à envoyer</h3>
                    <p className="text-sm text-blue-700">
                      {convocationStatus.days_until_ag} jours avant l'AG (minimum requis : 21 jours)
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      {documents.length} document(s) à envoyer aux coproprietaires
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="text-amber-600" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-800">Délai légal non respecté</h3>
                    <p className="text-sm text-amber-700">
                      {convocationStatus.days_until_ag !== null 
                        ? `Seulement ${convocationStatus.days_until_ag} jour(s) avant l'AG`
                        : 'Date de l\'AG non définie'}
                    </p>
                    <p className="text-xs text-amber-600 mt-1">
                      La convocation doit être envoyée au minimum 21 jours avant l'AG
                    </p>
                  </div>
                </>
              )}
            </div>
            
            {/* Send Button */}
            {!convocationStatus?.convocation_sent && (
              <button
                onClick={() => setShowConfirmModal(true)}
                disabled={!convocationStatus?.can_send_now || sendingConvocation || documents.length === 0}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
                  convocationStatus?.can_send_now && documents.length > 0
                    ? 'bg-[#00D3A8] hover:bg-[#00C7A1] text-white shadow-md hover:shadow-lg'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
                data-testid="send-convocation-btn"
              >
                <Send size={20} />
                <span>Envoyer la convocation</span>
              </button>
            )}
          </div>
          
          {/* Warning if no documents */}
          {!convocationStatus?.convocation_sent && documents.length === 0 && (
            <div className="mt-3 p-3 bg-white/50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-700">
                ⚠️ Ajoutez au moins un document (ordre du jour, convocation...) avant d'envoyer.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Locked documents notice */}
      {convocationStatus?.documents_locked && (
        <div className="bg-gray-100 rounded-lg p-3 flex items-center space-x-3">
          <Lock className="text-gray-500" size={20} />
          <p className="text-sm text-gray-600">
            Les documents sont verrouillés suite à l'envoi de la convocation. Aucune modification n'est possible.
          </p>
        </div>
      )}

      {/* Documents header with download all button */}
      {documents.length > 0 && (
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-gray-700">
            {documents.length} document{documents.length > 1 ? 's' : ''}
          </h3>
          <button
            onClick={handleDownloadAll}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-[#00D3A8] text-white rounded-lg hover:bg-[#00BFA6] transition-colors"
            data-testid="download-all-btn"
          >
            <Download size={16} />
            Tout télécharger
          </button>
        </div>
      )}

      {/* Documents grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {documents.map((doc) => (
          <div 
            key={doc.id} 
            className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-white"
          >
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-1">
                {getFileIcon(doc.original_name)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate" title={doc.original_name}>
                  {doc.original_name}
                </p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(doc.file_size)}
                </p>
                <p className="text-xs text-gray-400">
                  {new Date(doc.uploaded_at).toLocaleDateString('fr-FR')}
                </p>
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-2 mt-3 pt-3 border-t border-gray-100">
              <button
                onClick={() => handleDownload(doc)}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Télécharger"
              >
                <Download size={16} />
              </button>
              {(userRole === 'gestionnaire' || userRole === 'directeur_cabinet') && !convocationStatus?.documents_locked && (
                <button
                  onClick={() => handleDelete(doc.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Supprimer"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Empty state */}
      {documents.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <FileText className="mx-auto mb-2 text-gray-300" size={48} />
          <p>Aucun document</p>
        </div>
      )}

      {/* Upload zone (gestionnaire only, if not locked) */}
      {(userRole === 'gestionnaire' || userRole === 'directeur_cabinet') && !convocationStatus?.documents_locked && (
        <div
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
            dragActive 
              ? 'border-[#00D3A8] bg-[#00D3A8]/5' 
              : 'border-gray-300 hover:border-[#00D3A8]'
          } ${uploading ? 'pointer-events-none opacity-60' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-upload').click()}
        >
          <input
            id="file-upload"
            type="file"
            multiple
            className="hidden"
            onChange={handleFileInput}
          />
          
          {uploading ? (
            <div>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00D3A8] mx-auto mb-2"></div>
              <p className="text-gray-600">Upload en cours... {uploadProgress}%</p>
              <div className="w-48 bg-gray-200 rounded-full h-2 mx-auto mt-2">
                <div 
                  className="bg-[#00D3A8] h-2 rounded-full transition-all" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          ) : (
            <>
              <Upload className={`mx-auto mb-2 ${dragActive ? 'text-[#00D3A8]' : 'text-gray-400'}`} size={32} />
              <p className="text-gray-600 font-medium">
                {dragActive ? 'Déposez les fichiers ici' : 'Cliquez ou glissez pour ajouter des documents'}
              </p>
              <p className="text-gray-400 text-sm mt-1">
                Tous formats acceptés • Maximum 100 Mo par fichier
              </p>
            </>
          )}
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Send className="text-blue-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Envoyer la convocation ?</h3>
              <p className="text-gray-600">
                Cette action enverra un email avec les documents joints à tous les coproprietaires de l'immeuble.
              </p>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
                <div className="text-sm text-amber-800">
                  <p className="font-semibold mb-1">Attention : action irréversible</p>
                  <ul className="list-disc list-inside space-y-1 text-amber-700">
                    <li>Les documents seront verrouillés et ne pourront plus être modifiés</li>
                    <li>La date d'envoi sera enregistrée pour le délai légal</li>
                    <li>{documents.length} document(s) seront envoyés en pièce jointe</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                disabled={sendingConvocation}
              >
                Annuler
              </button>
              <button
                onClick={handleSendConvocation}
                disabled={sendingConvocation}
                className="flex-1 px-4 py-3 bg-[#00D3A8] text-white rounded-lg font-medium hover:bg-[#00C7A1] transition-colors flex items-center justify-center space-x-2"
                data-testid="confirm-send-convocation-btn"
              >
                {sendingConvocation ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Envoi en cours...</span>
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    <span>Confirmer l'envoi</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AGDocumentsTab;
